import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { 
  Menu, X, ChevronDown, User, Settings, LogOut, 
  LayoutDashboard, MessageSquare, Wallet,
  Briefcase, Search, HelpCircle, RefreshCw, Bell, Heart
} from 'lucide-react';
import { useUser } from '../contexts/UserContext';
import { useIsNativeApp } from '../utils/platform';
import { useTranslation } from 'react-i18next';
import MobileHeader from './mobile/MobileHeader';
import LanguageToggle from './LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Navbar = ({ user: propUser, onLogout, onUserUpdate }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isNativeApp = useIsNativeApp();
  const { t } = useTranslation();
  const { user: contextUser, switchRole, logout: contextLogout, updateUser } = useUser();
  const [showRoleSwitcher, setShowRoleSwitcher] = useState(false);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [bellOpen, setBellOpen] = useState(false);
  const [notifFeed, setNotifFeed] = useState([]);
  const prevTicketCountRef = React.useRef(null);
  const notifPermissionRef = React.useRef(false);

  // Become a Provider modal state
  const [showBecomeProviderModal, setShowBecomeProviderModal] = useState(false);

  // Mobile drawer can request opening the Become-a-Provider modal
  useEffect(() => {
    const handler = () => setShowBecomeProviderModal(true);
    window.addEventListener('gj:openBecomeProvider', handler);
    return () => window.removeEventListener('gj:openBecomeProvider', handler);
  }, []);
  const [clearanceFile, setClearanceFile] = useState(null);
  const [clearancePreview, setClearancePreview] = useState(null);
  const [uploadingClearance, setUploadingClearance] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeError, setUpgradeError] = useState('');
  const [clearanceRequired, setClearanceRequired] = useState(true);
  
  // Badge counts for red dot indicators
  const [badgeCounts, setBadgeCounts] = useState({
    unread_messages: 0,
    pending_bookings: 0,
    new_bids: 0,
    pending_withdrawals: 0,
    unread_notifications: 0,
    admin_actionable: 0,
    active_disputes: 0
  });

  // Use context user if available, fallback to prop user
  const user = contextUser || propUser;

  const effectiveRole = user?.active_role || user?.role;
  const roleLabel = (r) => r === 'client' ? t('ux.roleClient') : r === 'provider' ? t('ux.roleProvider') : r;
  
  // Helper function to check if a path is active
  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };
  
  // Get link classes with active state
  const getLinkClasses = (path) => {
    const baseClasses = "px-4 py-2 rounded-full font-medium transition";
    if (isActive(path)) {
      return `${baseClasses} bg-gray-900 text-white`;
    }
    return `${baseClasses} text-gray-600 hover:text-gray-900 hover:bg-gray-50`;
  };
  
  // Get mobile link classes with active state
  const getMobileLinkClasses = (path) => {
    const baseClasses = "flex items-center gap-3 px-3 py-2 rounded-lg";
    if (isActive(path)) {
      return `${baseClasses} bg-gray-900 text-white`;
    }
    return `${baseClasses} text-gray-700 hover:bg-gray-50`;
  };

  // Check if user can become a provider (client-only user without provider role)
  const canBecomeProvider = user && !user.is_owner &&
    user.available_roles && !user.available_roles.includes('provider') &&
    user.available_roles.includes('client');

  // Request browser notification permission for admins
  useEffect(() => {
    // Fetch platform config for clearance requirement
    if (canBecomeProvider) {
      axios.get(`${API}/platform/config`).then(res => {
        setClearanceRequired(res.data.require_barangay_clearance !== false);
      }).catch(() => {});
    }
  }, [canBecomeProvider]);
  useEffect(() => {
    const isAdmin = effectiveRole === 'admin' || user?.is_owner;
    if (user && isAdmin && 'Notification' in window) {
      if (Notification.permission === 'default') {
        Notification.requestPermission().then(perm => {
          notifPermissionRef.current = perm === 'granted';
        });
      } else {
        notifPermissionRef.current = Notification.permission === 'granted';
      }
    }
  }, [user, effectiveRole]);

  useEffect(() => {
    if (user) {
      fetchBadgeCounts();
      const interval = setInterval(fetchBadgeCounts, 30000);
      
      // Listen for refresh events from other components
      const handleRefresh = () => fetchBadgeCounts();
      window.addEventListener('refreshBadgeCounts', handleRefresh);
      
      return () => {
        clearInterval(interval);
        window.removeEventListener('refreshBadgeCounts', handleRefresh);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, effectiveRole]);

  const fetchBadgeCounts = async () => {
    try {
      const response = await axios.get(`${API}/badge-counts`);
      const data = response.data;
      setBadgeCounts(data);

      // Browser push notification for new support tickets (admin only)
      const isAdmin = effectiveRole === 'admin' || user?.is_owner;
      if (isAdmin && data.open_support_tickets > 0) {
        const prev = prevTicketCountRef.current;
        if (prev !== null && data.open_support_tickets > prev && notifPermissionRef.current && document.hidden) {
          new Notification('New Support Ticket', {
            body: `You have ${data.open_support_tickets} open support ticket${data.open_support_tickets > 1 ? 's' : ''} awaiting response.`,
            icon: '/favicon.ico',
            tag: 'support-ticket',
          });
        }
        prevTicketCountRef.current = data.open_support_tickets;
      }
    } catch (error) {
      console.error('Error fetching badge counts:', error);
    }
  };

  const fetchNotifFeed = async () => {
    try {
      const res = await axios.get(`${API}/admin/notification-feed`);
      setNotifFeed(res.data);
    } catch {}
  };

  const handleLogout = () => {
    // Clear context state first
    if (contextLogout) {
      contextLogout();
    }
    // Then call the parent's onLogout to clear App.js state
    onLogout();
    navigate('/');
  };

  const getAvailableRoles = () => {
    if (!user) return [];
    
    if (user.available_roles && user.available_roles.length > 0) {
      return user.available_roles;
    }
    
    const roles = [user.role];
    if (user.is_owner || user.email === 'chitopargas@gmail.com') {
      if (!roles.includes('admin')) roles.push('admin');
    }
    if (!roles.includes('client')) roles.push('client');
    if (!roles.includes('provider')) roles.push('provider');
    return [...new Set(roles)];
  };

  const availableRoles = getAvailableRoles();
  const hasMultipleRoles = availableRoles.length > 1;

  const handleRoleSwitch = async (newRole) => {
    if (newRole === effectiveRole || switchingRole) return;
    
    setSwitchingRole(true);
    try {
      // Use context's switchRole if available
      if (switchRole) {
        await switchRole(newRole);
      } else {
        // Fallback to direct API call
        const response = await axios.put(`${API}/user/switch-role?new_role=${newRole}`);
        if (updateUser) {
          updateUser(response.data);
        } else if (onUserUpdate) {
          onUserUpdate(response.data);
        }
      }
      setShowRoleSwitcher(false);
      // Navigate to appropriate dashboard based on new role
      if (newRole === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error switching role:', error);
      alert(error.response?.data?.detail || t('provider.switchRoleFailed'));
    } finally {
      setSwitchingRole(false);
    }
  };

  const handleClearanceFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setUpgradeError(t('provider.fileTooLarge'));
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setUpgradeError(t('provider.fileWrongType'));
      return;
    }
    setClearanceFile(file);
    setUpgradeError('');
    const reader = new FileReader();
    reader.onloadend = () => setClearancePreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleBecomeProvider = async () => {
    setUpgrading(true);
    setUpgradeError('');
    const token = localStorage.getItem('token');

    try {
      // Step 1: Upload clearance if file was selected and required
      if (clearanceRequired && clearanceFile) {
        const uploadData = new FormData();
        uploadData.append('file', clearanceFile);
        uploadData.append('image_type', 'clearance');

        const uploadRes = await axios.post(`${API}/upload/id-image`, uploadData, {
          headers: { 'Content-Type': 'multipart/form-data', 'Authorization': `Bearer ${token}` }
        });

        // Save clearance to profile
        await axios.post(`${API}/kyc/barangay-clearance`,
          { clearance_url: uploadRes.data.url },
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
      }

      // Step 2: Add provider role
      await axios.post(`${API}/user/add-role?new_role=provider`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Step 3: Switch to provider role
      const switchRes = await axios.put(`${API}/user/switch-role?new_role=provider`, {}, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Step 4: Update user state
      if (updateUser) {
        updateUser(switchRes.data);
      } else if (onUserUpdate) {
        onUserUpdate(switchRes.data);
      }

      // Step 5: Refresh user data
      const meRes = await axios.get(`${API}/auth/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (updateUser) updateUser(meRes.data);

      setShowBecomeProviderModal(false);
      setClearanceFile(null);
      setClearancePreview(null);
      navigate('/dashboard');
    } catch (err) {
      console.error('Become provider error:', err);
      setUpgradeError(err.response?.data?.detail || 'Failed to upgrade to provider. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: 'bg-purple-100 text-purple-700',
      provider: 'bg-blue-100 text-blue-700',
      client: 'bg-green-100 text-green-700'
    };
    return styles[role] || 'bg-gray-100 text-gray-700';
  };

  return (
    <>
    {isNativeApp ? (
      <MobileHeader user={user} unreadCount={badgeCounts.unread_notifications || 0} />
    ) : (
    <nav className="bg-white border-b border-gray-100" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <img 
              src="/logo-dark.png" 
              alt="GoodJobPH" 
              className="h-9 w-9 object-contain"
            />
            <span className="text-xl font-bold text-gray-900">GoodJob</span>
          </Link>

          {/* Quick language switch (visible on all widths) */}
          <LanguageToggle className="ml-2 sm:ml-3" />

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-1">
            {/* Admin-specific navigation */}
            {user && effectiveRole === 'admin' ? (
              <>
                <Link 
                  to="/profile" 
                  className={getLinkClasses('/profile')}
                  data-testid="navbar-profile"
                >
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {t('nav.profile')}
                  </span>
                </Link>

                <Link 
                  to="/account-settings" 
                  className={getLinkClasses('/account-settings')}
                  data-testid="navbar-settings"
                >
                  <span className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    {t('nav.accountSettings')}
                  </span>
                </Link>

                <Link 
                  to="/admin" 
                  className={getLinkClasses('/admin')}
                  data-testid="navbar-admin-dashboard"
                >
                  <span className="flex items-center gap-2 relative">
                    <LayoutDashboard className="w-4 h-4" />
                    {t('nav.adminDashboard')}
                    {/* Red dot for admin-actionable items (withdrawals, KYC, disputes) */}
                    {badgeCounts.admin_actionable > 0 && (
                      <span className="absolute -top-1 -right-3 w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </span>
                </Link>

                <Link 
                  to="/owner" 
                  className={getLinkClasses('/owner')}
                  data-testid="navbar-owner-panel"
                >
                  <span className="flex items-center gap-2">
                    <Briefcase className="w-4 h-4" />
                    {t('nav.ownerPanel')}
                  </span>
                </Link>
              </>
            ) : (
              <>
                {/* Client/Provider navigation */}
                {effectiveRole === 'client' || !user ? (
                  <Link 
                    to="/services" 
                    className={getLinkClasses('/services')}
                    data-testid="navbar-browse-services"
                  >
                    <span className="flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      {t('nav.browseServices')}
                    </span>
                  </Link>
                ) : effectiveRole === 'provider' ? (
                  <Link 
                    to="/jobs" 
                    className={getLinkClasses('/jobs')}
                    data-testid="navbar-browse-jobs"
                  >
                    <span className="flex items-center gap-2">
                      <Search className="w-4 h-4" />
                      {t('nav.browseJobs')}
                    </span>
                  </Link>
                ) : null}

                {user ? (
                  <>
                    <Link 
                      to="/dashboard" 
                      className={getLinkClasses('/dashboard')}
                      data-testid="navbar-dashboard"
                    >
                      <span className="flex items-center gap-2">
                        <LayoutDashboard className="w-4 h-4" />
                        {t('nav.dashboard')}
                      </span>
                    </Link>

                    {/* My Services - Provider only */}
                    {effectiveRole === 'provider' && (
                      <Link 
                        to="/my-services" 
                        className={getLinkClasses('/my-services')}
                        data-testid="navbar-my-services"
                      >
                        <span className="flex items-center gap-2">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                          </svg>
                          {t('nav.myServices')}
                        </span>
                      </Link>
                    )}

                    {/* My Bookings (Provider) / Booked & Posted Jobs (Client) */}
                    <Link 
                      to="/my-bookings" 
                      className={`${getLinkClasses('/my-bookings')} relative`}
                      data-testid="navbar-my-bookings"
                    >
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        {effectiveRole === 'client' ? t('nav.bookedPostedJobs') : t('nav.myBidsBookings')}
                      </span>
                      {(badgeCounts.pending_bookings > 0 || badgeCounts.new_bids > 0 || badgeCounts.active_disputes > 0) && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full" />
                      )}
                    </Link>

                    <Link 
                      to="/inquiries" 
                      className={`${getLinkClasses('/inquiries')} relative`}
                      data-testid="navbar-inquiries"
                    >
                      <span className="flex items-center gap-2">
                        <MessageSquare className="w-4 h-4" />
                        {t('nav.messages')}
                      </span>
                      {/* Red dot for unread messages */}
                      {badgeCounts.unread_messages > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full" />
                      )}
                    </Link>
                  </>
                ) : null}
              </>
            )}

            {/* Role Switcher - only for logged-in users */}
            {user && (
              <>
                {/* Role Switcher */}
                {hasMultipleRoles && (
                  <div className="relative">
                    <button
                      onClick={() => setShowRoleSwitcher(!showRoleSwitcher)}
                      className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-full font-medium transition flex items-center gap-2"
                      data-testid="role-switcher-btn"
                    >
                      <RefreshCw className="w-4 h-4" />
                      <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadge(effectiveRole)}`}>
                        {roleLabel(effectiveRole)}
                      </span>
                      <ChevronDown className={`w-4 h-4 transition ${showRoleSwitcher ? 'rotate-180' : ''}`} />
                    </button>

                    {showRoleSwitcher && (
                      <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-50">
                        <div className="px-3 py-2 text-xs text-gray-500 border-b border-gray-100">
                          {t('nav.switchRole')}
                        </div>
                        {availableRoles.map(role => (
                          <button
                            key={role}
                            onClick={() => handleRoleSwitch(role)}
                            disabled={switchingRole || role === effectiveRole}
                            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition ${
                              role === effectiveRole 
                                ? 'bg-gray-50 text-gray-400' 
                                : 'text-gray-700 hover:bg-gray-50'
                            }`}
                            data-testid={`switch-to-${role}`}
                          >
                            <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadge(role)}`}>
                              {roleLabel(role)}
                            </span>
                            {role === effectiveRole && <span className="text-xs text-gray-400">({t('nav.current')})</span>}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Admin Notification Bell */}
                {user && (effectiveRole === 'admin' || user.is_owner) && (
                  <div className="relative ml-2">
                    <button
                      onClick={() => { setBellOpen(!bellOpen); if (!bellOpen) fetchNotifFeed(); }}
                      className="relative p-2 rounded-full hover:bg-gray-100 transition"
                      data-testid="admin-notif-bell"
                    >
                      <Bell className="w-5 h-5 text-gray-600" />
                      {(badgeCounts.open_support_tickets > 0 || badgeCounts.active_disputes > 0 || badgeCounts.pending_kyc > 0) && (
                        <span className="absolute top-1 right-1 min-w-[16px] h-4 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="admin-notif-count">
                          {(() => { const total = (badgeCounts.open_support_tickets || 0) + (badgeCounts.active_disputes || 0) + (badgeCounts.pending_kyc || 0); return total > 99 ? '99+' : total; })()}
                        </span>
                      )}
                    </button>

                    {bellOpen && (
                      <>
                        <div className="fixed inset-0 z-40" onClick={() => setBellOpen(false)} />
                        <div className="absolute right-0 mt-2 w-80 bg-white border border-gray-100 rounded-xl shadow-xl z-50 overflow-hidden" data-testid="admin-notif-dropdown">
                          <div className="px-4 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                            <span className="font-semibold text-gray-800 text-sm">{t('nav.notifications')}</span>
                            {badgeCounts.open_support_tickets > 0 && (
                              <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full">{badgeCounts.open_support_tickets} open</span>
                            )}
                          </div>
                          <div className="max-h-80 overflow-y-auto">
                            {notifFeed.length === 0 ? (
                              <div className="py-8 text-center text-gray-400 text-sm">{t('nav.noNotifications')}</div>
                            ) : (
                              notifFeed.slice(0, 15).map(n => (
                                <button
                                  key={`${n.type}-${n.id}`}
                                  onClick={() => {
                                    setBellOpen(false);
                                    if ((n.type === 'dispute_opened' || n.type === 'dispute_escalated') && n.dispute_id) {
                                      navigate(`/disputes/${n.dispute_id}`);
                                    } else if (n.type === 'kyc_submission' || n.type === 'kyc_resubmission' || n.type === 'kyc_pending' || n.type === 'kyc_verification_needed') {
                                      navigate('/admin/id-verification');
                                    } else {
                                      navigate('/owner');
                                    }
                                  }}
                                  className="w-full text-left px-4 py-3 hover:bg-gray-50 border-b border-gray-50 transition"
                                >
                                  <div className="flex items-start gap-3">
                                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 bg-orange-100 text-orange-600">
                                      <MessageSquare className="w-4 h-4" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="text-sm font-medium text-gray-800 truncate">{n.title}</div>
                                      <div className="text-xs text-gray-500 truncate">{n.detail}</div>
                                      <div className="text-[10px] text-gray-400 mt-0.5">{n.created_at ? new Date(n.created_at).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}</div>
                                    </div>
                                  </div>
                                </button>
                              ))
                            )}
                          </div>
                          <div className="px-4 py-2 bg-gray-50 border-t text-center">
                            <button onClick={() => { setBellOpen(false); navigate('/owner'); }} className="text-xs text-orange-500 hover:text-orange-600 font-medium">
                              {t('nav.viewAllOwnerPanel')}
                            </button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                )}

                {/* User Menu */}
                <div className="relative ml-2">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="flex items-center gap-2 p-1.5 rounded-full hover:bg-gray-50 transition"
                    data-testid="user-menu-btn"
                  >
                    {user.avatar ? (
                      <img 
                        src={user.avatar.startsWith('/api') ? `${BACKEND_URL}${user.avatar}` : user.avatar}
                        alt={user.name}
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-900 flex items-center justify-center text-white text-sm font-medium">
                        {user.name?.charAt(0)?.toUpperCase()}
                      </div>
                    )}
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition ${userMenuOpen ? 'rotate-180' : ''}`} />
                  </button>

                  {userMenuOpen && (
                    <div className="absolute right-0 mt-2 w-56 bg-white border border-gray-100 rounded-xl shadow-lg py-2 z-50">
                      <div className="px-4 py-3 border-b border-gray-100">
                        <p className="font-semibold text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                      </div>
                      
                      {effectiveRole === 'provider' && (
                        <>
                          <Link 
                            to="/earnings" 
                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                            data-testid="navbar-earnings"
                          >
                            <Wallet className="w-4 h-4" />
                            {t('nav.earnings')}
                          </Link>
                          <Link 
                            to="/my-bids" 
                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                            data-testid="navbar-my-bids"
                          >
                            <Briefcase className="w-4 h-4" />
                            {t('nav.myBids')}
                          </Link>
                        </>
                      )}
                      
                      {effectiveRole === 'client' && (
                        <Link 
                          to="/payments" 
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="navbar-payments"
                        >
                          <Wallet className="w-4 h-4" />
                          {t('nav.payments')}
                        </Link>
                      )}

                      {(user.is_admin || user.is_owner || user.email === 'chitopargas@gmail.com') && effectiveRole === 'admin' && (
                        <>
                          <Link 
                            to="/admin" 
                            className="flex items-center gap-3 px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 relative"
                            onClick={() => setUserMenuOpen(false)}
                            data-testid="navbar-admin"
                          >
                            <Settings className="w-4 h-4" />
                            {t('nav.adminDashboard')}
                            {/* Red dot for admin-actionable items */}
                            {badgeCounts.admin_actionable > 0 && (
                              <span className="absolute top-2 left-6 w-2 h-2 bg-red-500 rounded-full" />
                            )}
                          </Link>
                          {(user.is_owner || user.email === 'chitopargas@gmail.com') && (
                            <Link 
                              to="/owner" 
                              className="flex items-center gap-3 px-4 py-2 text-sm text-amber-600 hover:bg-amber-50"
                              onClick={() => setUserMenuOpen(false)}
                              data-testid="navbar-owner"
                            >
                              <Settings className="w-4 h-4" />
                              {t('nav.ownerPanel')}
                            </Link>
                          )}
                        </>
                      )}

                      <div className="border-t border-gray-100 mt-1 pt-1">
                        {effectiveRole !== 'admin' && (
                          <Link 
                            to="/favorites" 
                            className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setUserMenuOpen(false)}
                            data-testid="navbar-favorites"
                          >
                            <Heart className="w-4 h-4" />
                            {t('nav.favorites')}
                          </Link>
                        )}
                        <Link 
                          to="/profile" 
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="navbar-profile"
                        >
                          <User className="w-4 h-4" />
                          {t('nav.profile')}
                        </Link>
                        <Link 
                          to="/account-settings" 
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="navbar-settings"
                        >
                          <Settings className="w-4 h-4" />
                          {t('nav.settings')}
                        </Link>
                        <Link 
                          to="/help" 
                          className="flex items-center gap-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          onClick={() => setUserMenuOpen(false)}
                          data-testid="navbar-help"
                        >
                          <HelpCircle className="w-4 h-4" />
                          {t('nav.help')}
                        </Link>
                        {canBecomeProvider && (
                          <button
                            onClick={() => { setShowBecomeProviderModal(true); setUserMenuOpen(false); }}
                            className="w-full flex items-center gap-3 px-4 py-2 text-sm text-orange-600 hover:bg-orange-50 font-medium"
                            data-testid="navbar-become-provider"
                          >
                            <Briefcase className="w-4 h-4" />
                            {t('nav.becomeProvider')}
                          </button>
                        )}
                        <button
                          onClick={() => { handleLogout(); setUserMenuOpen(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          data-testid="navbar-logout"
                        >
                          <LogOut className="w-4 h-4" />
                          {t('nav.logout')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Login/Signup for non-logged in users */}
            {!user && (
              <div className="flex items-center gap-2">
                <Link 
                  to="/login" 
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 rounded-full font-medium transition"
                  data-testid="navbar-login"
                >
                  {t('nav.logIn')}
                </Link>
                <Link 
                  to="/register" 
                  className="px-5 py-2 bg-gray-900 text-white rounded-full font-medium hover:bg-gray-800 transition"
                  data-testid="navbar-register"
                >
                  {t('nav.signUp')}
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-gray-100 bg-white">
          <div className="px-4 py-3 space-y-1">
            {/* Admin-specific mobile navigation */}
            {user && effectiveRole === 'admin' ? (
              <>
                <Link 
                  to="/profile" 
                  className={getMobileLinkClasses('/profile')}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-navbar-profile"
                >
                  <User className="w-5 h-5" />
                  {t('nav.profile')}
                </Link>
                <Link 
                  to="/account-settings" 
                  className={getMobileLinkClasses('/account-settings')}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-navbar-settings"
                >
                  <Settings className="w-5 h-5" />
                  {t('nav.accountSettings')}
                </Link>
                <Link 
                  to="/admin" 
                  className={getMobileLinkClasses('/admin')}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-navbar-admin-dashboard"
                >
                  <span className="relative">
                    <LayoutDashboard className="w-5 h-5" />
                    {badgeCounts.admin_actionable > 0 && (
                      <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full" />
                    )}
                  </span>
                  {t('nav.adminDashboard')}
                </Link>
                <Link 
                  to="/owner" 
                  className={getMobileLinkClasses('/owner')}
                  onClick={() => setMobileMenuOpen(false)}
                  data-testid="mobile-navbar-owner-panel"
                >
                  <Briefcase className="w-5 h-5" />
                  {t('nav.ownerPanel')}
                </Link>
                {/* Mobile Role Switcher for Admin */}
                {hasMultipleRoles && (
                  <div className="border-t border-gray-100 mt-2 pt-2">
                    <div className="px-3 py-2 text-xs text-gray-500 font-medium">{t('nav.switchRole')}</div>
                    <div className="flex flex-wrap gap-2 px-3 py-1">
                      {availableRoles.map(role => (
                        <button
                          key={role}
                          onClick={() => { handleRoleSwitch(role); setMobileMenuOpen(false); }}
                          disabled={switchingRole || role === effectiveRole}
                          className={`px-3 py-1.5 text-sm rounded-full transition ${
                            role === effectiveRole 
                              ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                              : `${getRoleBadge(role)} cursor-pointer`
                          }`}
                          data-testid={`mobile-switch-to-${role}`}
                        >
                          {role} {role === effectiveRole && `(${t('nav.current')})`}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="border-t border-gray-100 mt-2 pt-2">
                  <button
                    onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                    data-testid="mobile-navbar-logout"
                  >
                    <LogOut className="w-5 h-5" />
                    {t('nav.logout')}
                  </button>
                </div>
              </>
            ) : (
              <>
                {/* Client/Provider mobile navigation */}
                {effectiveRole === 'client' || !user ? (
                  <Link 
                    to="/services" 
                    className={getMobileLinkClasses('/services')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Search className="w-5 h-5" />
                    {t('nav.browseServices')}
                  </Link>
                ) : effectiveRole === 'provider' ? (
                  <Link 
                    to="/jobs" 
                    className={getMobileLinkClasses('/jobs')}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Search className="w-5 h-5" />
                    {t('nav.browseJobs')}
                  </Link>
                ) : null}

                {user ? (
                  <>
                    <Link 
                      to="/dashboard" 
                      className={getMobileLinkClasses('/dashboard')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <LayoutDashboard className="w-5 h-5" />
                      {t('nav.dashboard')}
                    </Link>
                    
                    {/* My Services - Provider only (mobile) */}
                    {effectiveRole === 'provider' && (
                      <Link 
                        to="/my-services" 
                        className={getMobileLinkClasses('/my-services')}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        {t('nav.myServices')}
                      </Link>
                    )}
                    
                    {/* My Bookings / Booked & Posted Jobs (mobile) */}
                    <Link 
                      to="/my-bookings" 
                      className={`${getMobileLinkClasses('/my-bookings')} relative`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                      </svg>
                      {effectiveRole === 'client' ? t('nav.bookedPostedJobs') : t('nav.myBidsBookings')}
                      {/* Red dot for pending bookings, new bids, or active disputes */}
                      {(badgeCounts.pending_bookings > 0 || badgeCounts.new_bids > 0 || badgeCounts.active_disputes > 0) && (
                        <span className="w-2.5 h-2.5 bg-red-500 rounded-full ml-auto" />
                      )}
                    </Link>
                    
                    <Link 
                      to="/inquiries" 
                      className={`${getMobileLinkClasses('/inquiries')} relative`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <MessageSquare className="w-5 h-5" />
                      {t('nav.messages')}
                      {/* Red dot for unread messages */}
                      {badgeCounts.unread_messages > 0 && (
                        <span className="w-2.5 h-2.5 bg-red-500 rounded-full ml-auto" />
                      )}
                    </Link>
                    <Link 
                      to="/help" 
                      className={getMobileLinkClasses('/help')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <HelpCircle className="w-5 h-5" />
                      {t('nav.help')}
                    </Link>
                    <Link 
                      to="/account-settings" 
                      className={getMobileLinkClasses('/account-settings')}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <Settings className="w-5 h-5" />
                      {t('nav.settings')}
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      className="w-full flex items-center gap-3 px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <LogOut className="w-5 h-5" />
                      {t('nav.logout')}
                    </button>
                    {canBecomeProvider && (
                      <button
                        onClick={() => { setShowBecomeProviderModal(true); setMobileMenuOpen(false); }}
                        className="w-full flex items-center gap-3 px-3 py-2 text-orange-600 hover:bg-orange-50 rounded-lg font-medium"
                        data-testid="mobile-become-provider"
                      >
                        <Briefcase className="w-5 h-5" />
                        {t('nav.becomeProvider')}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="pt-3 border-t border-gray-100 space-y-2">
                    <Link 
                      to="/login" 
                      className="block w-full text-center px-4 py-2 text-gray-700 border border-gray-200 rounded-full"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.logIn')}
                    </Link>
                    <Link 
                      to="/register" 
                      className="block w-full text-center px-4 py-2 bg-gray-900 text-white rounded-full"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {t('nav.signUp')}
                    </Link>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </nav>
    )}

      {/* Click outside to close dropdowns */}
      {(showRoleSwitcher || userMenuOpen) && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => { setShowRoleSwitcher(false); setUserMenuOpen(false); }}
        />
      )}
      {showBecomeProviderModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60]" data-testid="become-provider-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <div className="text-center mb-5">
              <div className="w-14 h-14 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Briefcase className="w-7 h-7 text-orange-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900" data-testid="become-provider-title">{t('provider.title')}</h2>
              <p className="text-gray-500 text-sm mt-1">
                {t('provider.subtitle')}
              </p>
            </div>

            {/* Requirements checklist */}
            <div className="bg-gray-50 rounded-lg p-4 mb-5 space-y-2">
              <p className="text-sm font-semibold text-gray-700 mb-2">{t('provider.requirements')}</p>
              <div className="flex items-center gap-2 text-sm">
                {user?.id_verified ? (
                  <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
                ) : (
                  <span className="w-5 h-5 bg-red-100 text-red-500 rounded-full flex items-center justify-center text-xs font-bold">!</span>
                )}
                <span className={user?.id_verified ? 'text-green-700' : 'text-red-600'}>
                  {t('provider.idVerification')} {user?.id_verified ? t('provider.completed') : t('provider.required')}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                {user?.police_clearance || !clearanceRequired ? (
                  <span className="w-5 h-5 bg-green-100 text-green-600 rounded-full flex items-center justify-center text-xs font-bold">✓</span>
                ) : (
                  <span className="w-5 h-5 bg-orange-100 text-orange-500 rounded-full flex items-center justify-center text-xs font-bold">!</span>
                )}
                <span className={user?.police_clearance || !clearanceRequired ? 'text-green-700' : 'text-orange-600'}>
                  {t('provider.barangayClearance')} {!clearanceRequired ? t('provider.notRequired') : user?.police_clearance ? t('provider.completed') : t('provider.required')}
                </span>
              </div>
            </div>

            {/* ID Verification warning */}
            {!user?.id_verified && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
                {t('provider.idWarning')}
              </div>
            )}

            {/* Barangay Clearance Upload - only show if ID is verified and clearance required but not yet uploaded */}
            {user?.id_verified && clearanceRequired && !user?.police_clearance && (
              <div className="mb-5">
                <label className="block text-gray-700 text-sm font-semibold mb-2">{t('provider.uploadClearance')}</label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-orange-400 transition">
                  {clearancePreview ? (
                    <div className="relative inline-block">
                      <img src={clearancePreview} alt="Clearance preview" className="max-h-40 mx-auto rounded-lg" data-testid="become-provider-clearance-preview" />
                      <button
                        type="button"
                        onClick={() => { setClearanceFile(null); setClearancePreview(null); }}
                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ) : (
                    <label className="cursor-pointer block py-4">
                      <Briefcase className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                      <p className="text-gray-600 text-sm font-medium">{t('provider.clickUpload')}</p>
                      <p className="text-gray-400 text-xs mt-1">{t('provider.fileHint')}</p>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleClearanceFileSelect}
                        className="hidden"
                        data-testid="become-provider-file-input"
                      />
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Error */}
            {upgradeError && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4" data-testid="become-provider-error">
                {upgradeError}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowBecomeProviderModal(false);
                  setClearanceFile(null);
                  setClearancePreview(null);
                  setUpgradeError('');
                }}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-lg hover:bg-gray-50 font-semibold text-sm"
                data-testid="become-provider-cancel"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleBecomeProvider}
                disabled={
                  upgrading ||
                  !user?.id_verified ||
                  (clearanceRequired && !user?.police_clearance && !clearanceFile)
                }
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2.5 rounded-lg hover:shadow-lg font-semibold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="become-provider-submit"
              >
                {upgrading ? t('provider.upgrading') : t('provider.becomeProviderBtn')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
